// Live stroke-curve graph: boat acceleration or PM5 force (erg). Last 3 strokes
// overlaid (older fade), pinned "ideal" ghost dashed behind. Long-press pins last stroke.
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { PinOff } from 'lucide-react';
import { subscribe, getStrokes, lastFullStroke, compactStroke, getYRange, getExpectedDriveMs } from '@/lib/strokeCurveBuffer';
import { loadIdealCurves, getIdeal, subscribeIdeal, pinIdealCurve, clearIdealCurve } from '@/lib/idealCurves';
import { trimDrivePairs } from '@/lib/driveWindow';
import { getForceUnit, fromNewtons } from '@/lib/forceUnit';

const STROKE_COLOR = '#21B86A';
// Oldest → newest: 20 % / 50 % / 100 %, all the same colour.
const STROKE_STYLES = [
  { alpha: 0.2, width: 2, glow: 0 },
  { alpha: 0.5, width: 2.5, glow: 0 },
  { alpha: 1, width: 3.5, glow: 10 },
];
const GHOST_COLOR = '#F5C518';
const LONG_PRESS_MS = 700;

// X = % of the drive (catch → finish). Completed/finished drives use their own
// duration; a live drive uses the expected drive time and stops at 100 %.
function strokeXY(stroke, isLive, expectedMs) {
  const dur = stroke.dur || (isLive ? expectedMs : stroke.pts[stroke.pts.length - 1]?.dt) || 1;
  const out = [];
  for (const p of stroke.pts) {
    const f = p.dt / dur;
    if (f > 1) break;
    out.push([f, p.a]);
  }
  return out;
}

function drawCurves(canvas, strokes, ghost, yRange, expectedMs, hasLive) {
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  if (!w || !h) return;
  if (canvas.width !== w * dpr) { canvas.width = w * dpr; canvas.height = h * dpr; }
  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  const dim = getComputedStyle(canvas).getPropertyValue('--c-dim').trim() || '#6b7a88';
  const series = strokes.map((s, i) => strokeXY(s, hasLive && i === strokes.length - 1, expectedMs));
  const ghostXY = ghost && ghost.pts.length > 1 ? strokeXY(ghost, false, 1) : null;

  let lo; let hi;
  if (yRange) { [lo, hi] = yRange; } else {
    lo = 0; hi = 0.5;
    [...series, ghostXY || []].forEach((s) => s.forEach(([, v]) => { if (v < lo) lo = v; if (v > hi) hi = v; }));
    const pad = (hi - lo) * 0.1; lo = lo < 0 ? lo - pad : 0; hi += pad;
  }
  const y = (v) => 4 + ((hi - Math.max(lo, Math.min(hi, v))) / (hi - lo || 1)) * (h - 8);
  const x = (f) => f * w;
  const base = y(0);
  const path = (s) => { ctx.beginPath(); s.forEach(([f, v], j) => (j ? ctx.lineTo(x(f), y(v)) : ctx.moveTo(x(f), y(v)))); };

  ctx.save();
  ctx.strokeStyle = dim; ctx.globalAlpha = 0.6; ctx.setLineDash([4, 4]); ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(0, base); ctx.lineTo(w, base); ctx.stroke();
  ctx.restore();

  if (ghostXY) {
    ctx.save();
    ctx.strokeStyle = GHOST_COLOR; ctx.globalAlpha = 0.85; ctx.lineWidth = 2.5; ctx.setLineDash([7, 5]);
    path(ghostXY); ctx.stroke();
    ctx.restore();
  }

  const live = series[series.length - 1];
  if (live && live.length > 1) {
    [['#21B86A', (v) => Math.max(0, v)], ['#EF4444', (v) => Math.min(0, v)]].forEach(([c, clip]) => {
      ctx.save();
      ctx.globalAlpha = 0.14; ctx.fillStyle = c;
      ctx.beginPath();
      ctx.moveTo(x(live[0][0]), base);
      live.forEach(([f, v]) => ctx.lineTo(x(f), y(clip(v))));
      ctx.lineTo(x(live[live.length - 1][0]), base);
      ctx.closePath(); ctx.fill();
      ctx.restore();
    });
  }

  series.forEach((s, i) => {
    if (s.length < 2) return;
    const st = STROKE_STYLES[STROKE_STYLES.length - series.length + i];
    ctx.save();
    ctx.globalAlpha = st.alpha; ctx.strokeStyle = STROKE_COLOR; ctx.lineWidth = st.width; ctx.lineJoin = 'round';
    if (st.glow) { ctx.shadowColor = STROKE_COLOR; ctx.shadowBlur = st.glow; }
    path(s); ctx.stroke();
    ctx.restore();
  });
}

const toStroke = (ideal, kind) => {
  if (!ideal?.points?.length) return null;
  const points = kind === 'boat' ? trimDrivePairs(ideal.points) : ideal.points;
  return { dur: points[points.length - 1][0] || 1, pts: points.map(([dt, a]) => ({ dt, a })) };
};

export default function AccelCurveGraph({ isErg = false }) {
  const kind = isErg ? 'erg' : 'boat';
  const canvasRef = useRef(null);
  const peakRef = useRef(null);
  const dirtyRef = useRef(true);
  const pressRef = useRef(null);
  const originRef = useRef({ x: 0, y: 0 });
  const [hasIdeal, setHasIdeal] = useState(!!getIdeal(kind));
  const [forceUnit, setForceUnit] = useState(getForceUnit);
  const unitRef = useRef(forceUnit);

  useEffect(() => {
    const onUnit = () => { const u = getForceUnit(); unitRef.current = u; setForceUnit(u); dirtyRef.current = true; };
    window.addEventListener('crewz-force-unit-changed', onUnit);
    return () => window.removeEventListener('crewz-force-unit-changed', onUnit);
  }, []);

  useEffect(() => {
    loadIdealCurves(true);
    const offIdeal = subscribeIdeal(() => { setHasIdeal(!!getIdeal(kind)); dirtyRef.current = true; });
    const off = subscribe(() => { dirtyRef.current = true; });
    dirtyRef.current = true;
    let raf;
    const loop = () => {
      if (dirtyRef.current && canvasRef.current) {
        dirtyRef.current = false;
        const strokes = getStrokes(kind);
        drawCurves(canvasRef.current, strokes, toStroke(getIdeal(kind), kind), getYRange(kind), getExpectedDriveMs(), kind === 'boat');
        const ref = lastFullStroke(kind)?.pts || [];
        const peak = ref.length ? Math.max(...ref.map((p) => p.a)) : null;
        if (peakRef.current) peakRef.current.textContent = peak == null ? '—' : (isErg ? fromNewtons(peak, unitRef.current).toFixed(0) : peak.toFixed(1));
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); off(); offIdeal(); };
  }, [kind, isErg]);

  const pinLast = async () => {
    const s = lastFullStroke(kind);
    if (!s) { toast.info('Row a full stroke first, then hold to pin it'); return; }
    try {
      await pinIdealCurve(kind, compactStroke(s, 120));
      if (navigator.vibrate) navigator.vibrate(60);
      toast.success(`Pinned as your ideal ${isErg ? 'erg' : 'boat'} stroke`);
    } catch (_e) {
      toast.error('Could not pin stroke');
    }
  };
  const unpin = async (e) => {
    e.stopPropagation();
    try {
      await clearIdealCurve(kind);
      toast.success('Ideal curve removed');
    } catch (_e) {
      toast.error('Could not remove ideal');
    }
  };
  const stop = (e) => e.stopPropagation();
  const pressStart = (e) => {
    e.stopPropagation();
    const t = e.touches?.[0] || e;
    originRef.current = { x: t.clientX, y: t.clientY };
    clearTimeout(pressRef.current);
    pressRef.current = setTimeout(pinLast, LONG_PRESS_MS);
  };
  const pressEnd = (e) => { e.stopPropagation(); clearTimeout(pressRef.current); };
  // Ignore small finger jitter (moving boat) — only cancel past ~10px
  const pressMove = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    if (Math.hypot(t.clientX - originRef.current.x, t.clientY - originRef.current.y) > 10) pressEnd(e);
  };

  const unit = isErg ? forceUnit.toUpperCase() : 'M/S²';
  return (
    <div
      className="crewz-accel-wrap"
      data-testid="accel-curve-graph"
      data-kind={kind}
      onTouchStart={pressStart}
      onTouchEnd={pressEnd}
      onTouchMove={pressMove}
      onMouseDown={pressStart}
      onMouseUp={pressEnd}
      onMouseLeave={pressEnd}
      onContextMenu={(e) => e.preventDefault()}
    >
      <style>{ACCEL_CSS}</style>
      <div className="crewz-accel-head">
        <span>{isErg ? 'PM5 FORCE CURVE · 3 STROKES' : 'ACCELERATION · DRIVE · 3 STROKES'}</span>
        <span>PEAK <strong ref={peakRef} data-testid="accel-curve-peak">—</strong> <span data-testid="accel-curve-unit">{unit}</span></span>
      </div>
      <div className="crewz-accel-plot">
        {isErg && <span className="crewz-accel-axis" data-testid="force-curve-axis-unit">{forceUnit}</span>}
        <canvas ref={canvasRef} className="crewz-accel-canvas" data-testid="accel-curve-canvas" />
      </div>
      <div className="crewz-accel-legend">
        <span className="hint" data-testid="accel-curve-pin-hint">HOLD TO PIN IDEAL</span>
        {hasIdeal && <span className="ghost" data-testid="accel-curve-ideal-legend">- - IDEAL</span>}
        {hasIdeal && (
          <button
            type="button"
            className="unpin"
            onClick={unpin}
            onMouseDown={stop}
            onMouseUp={stop}
            onTouchStart={stop}
            onTouchEnd={stop}
            data-testid="accel-curve-unpin-btn"
          >
            <PinOff size={13} /> UNPIN
          </button>
        )}
        {isErg ? <span className="drive">DRIVE</span> : (<><span className="drive">DRIVE</span><span className="check">CHECK</span></>)}
      </div>
    </div>
  );
}

const ACCEL_CSS = `
.crewz-accel-wrap { width: 100%; min-width: min(100%, 560px); height: 100%; min-height: 150px; display: flex; flex-direction: column; gap: 4px; padding: 4px 6px; user-select: none; -webkit-user-select: none; -webkit-touch-callout: none; }
.crewz-accel-head { display: flex; justify-content: space-between; font-size: 10px; font-weight: 700; letter-spacing: 0.14em; color: var(--c-dim, #6b7a88); font-family: 'JetBrains Mono', ui-monospace, monospace; }
.crewz-accel-head strong { color: #21B86A; font-size: 13px; }
.crewz-accel-plot { position: relative; flex: 1 1 auto; display: flex; min-height: 120px; }
.crewz-accel-axis { position: absolute; left: 2px; top: 2px; font-size: 10px; font-weight: 700; color: var(--c-dim, #6b7a88); font-family: 'JetBrains Mono', ui-monospace, monospace; pointer-events: none; }
.crewz-accel-canvas { flex: 1 1 auto; width: 100%; min-height: 120px; display: block; }
.crewz-accel-legend { display: flex; gap: 12px; justify-content: flex-end; align-items: center; font-size: 9px; font-weight: 700; letter-spacing: 0.14em; }
.crewz-accel-legend .hint { margin-right: auto; color: var(--c-dim, #6b7a88); opacity: 0.7; }
.crewz-accel-legend .ghost { color: ${GHOST_COLOR}; }
.crewz-accel-legend .unpin { display: inline-flex; align-items: center; gap: 4px; min-height: 32px; padding: 6px 14px; border-radius: 999px; border: 1px solid ${GHOST_COLOR}; background: transparent; color: ${GHOST_COLOR}; font: inherit; font-size: 11px; letter-spacing: inherit; cursor: pointer; transition: background-color 0.15s, color 0.15s; }
.crewz-accel-legend .unpin:hover { background: ${GHOST_COLOR}; color: #111; }
.crewz-accel-legend .drive { color: #21B86A; }
.crewz-accel-legend .check { color: #EF4444; }
`;
